import {makeAutoObservable} from 'mobx';

import {i18n} from '@yourcoach/i18n';

import {atob} from '../base64';
import {logger} from '../logger';
import {platform} from '../platform';
import {reloadApp} from '../reloadApp';

export const STORAGE_HEADER_NAME = 'x-storage-key';

/**
 * Class to store token to access S3 resources
 */
class StorageToken {
  token = '';

  constructor() {
    makeAutoObservable(this, undefined, {
      autoBind: true,
    });
  }

  checkIsUrlExpired(url: string) {
    if (this.isStorageUrl(url)) {
      const [_, search] = url.split('?');

      const urlParams = new URLSearchParams(search);

      // sign is JWT token contains expiration date in exp field
      const sign = urlParams.get('sign');

      if (!sign) {
        return false;
      }

      // decode sign to get expiration date
      const [body] = sign.split('.');

      try {
        const decodedBody = JSON.parse(atob(body)) as {exp?: number};

        const exp = decodedBody.exp
          ? new Date(decodedBody.exp * 1000)
          : new Date('3000-01-01');

        if (exp < new Date()) {
          const alert = require('../../components/alert').alert;

          alert.show({
            buttons: [
              {
                label: platform.isWeb
                  ? i18n.t('shared:refresh')
                  : i18n.t('shared:reload'),
                onClick: () => {
                  reloadApp();
                },
              },
            ],
            isDismissable: true,
            message: platform.isWeb
              ? i18n.t('shared:refresh_page_to_view_document')
              : i18n.t('shared:reload_app_to_view_document'),
            title: i18n.t('shared:this_session_has_expired'),
          });

          return true;
        }
      } catch (error) {
        logger.error(error);

        return false;
      }
    }

    return false;
  }

  getCleanUrl(url: string) {
    if (this.isStorageUrl(url)) {
      const [urlWithoutSearch] = url.split('?');

      return urlWithoutSearch;
    }

    return url;
  }

  getHeadersForUrl(url: string) {
    if (this.isStorageUrl(url)) {
      return {
        [STORAGE_HEADER_NAME]: this.token,
      };
    }
  }

  isStorageUrl(url: string): boolean {
    const STORAGE_URL_REGEX =
      /^https:\/\/(?:bucket|media)(?:\.[A-Za-z0-9-]+)*\.yourcoach\.health\/.*/;

    return STORAGE_URL_REGEX.test(url);
  }

  setToken(token: string) {
    this.token = token;
  }

  signUrl(url: string) {
    if (this.isStorageUrl(url)) {
      const [urlWithoutSearch, search] = url.split('?');

      const urlParams = new URLSearchParams(search);

      urlParams.append(STORAGE_HEADER_NAME, this.token);

      return `${urlWithoutSearch}?${urlParams.toString()}`;
    }

    return url;
  }
}

const storageTokenManager = new StorageToken();

export {storageTokenManager};
